// @flow
import { get, some } from "lodash";
import type { RaceProgram, BettingInterest, RaceOdds } from "@tvg/types/Race";
import type { OperationComponent } from "@apollo/client";
import type { RaceCardProps } from "@fdr/types/ProgramPage";
import BetSelectionUtils from "@tvg/utils/betSelectionUtils";

const QUERY_VARIABLES = {
  trackAbbr: null,
  wagerProfile: "FDR-Generic",
  raceNumber: 0
};

const getBettingInterests = (races: RaceProgram[]) =>
  races
    ? races.map((race: RaceProgram) => get(race, "bettingInterests", []))
    : [];

const getDecimalOddsValue = ({ numerator, denominator }: RaceOdds) => {
  let decimal = denominator && numerator ? numerator / denominator : numerator;

  if (!decimal || isNaN(decimal)) {
    decimal = Number.MAX_VALUE;
  }
  return decimal;
};

const getLowestOdd = (arr: BettingInterest[]) => {
  let lowestOdd = Number.MAX_VALUE;
  let myOdd = Number.MAX_VALUE;

  try {
    for (let i = 0; i < arr.length; i += 1) {
      if (some(arr[i].runners, (runner) => runner.scratched === false)) {
        myOdd = arr[i].currentOdds
          ? getDecimalOddsValue(arr[i].currentOdds)
          : myOdd;
        if (myOdd < lowestOdd) {
          lowestOdd = myOdd;
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
  return lowestOdd;
};

const isFavorite = (currentOdds, lowestOdd) => {
  const odd = currentOdds ? getDecimalOddsValue(currentOdds) : 99;
  return odd === lowestOdd && odd !== 99;
};

const isFavoriteBIScratched = (bettingInterests: BettingInterest[]) => {
  for (let i = 0; i < bettingInterests.length; i += 1) {
    if (bettingInterests[i].favorite) {
      const { runners } = bettingInterests[i];

      for (let j = 0; j < runners.length; j += 1) {
        if (!runners[j].scratched) {
          return false;
        }
      }
    }
  }
  return true;
};

export const isHongKongRaces = (code: string) =>
  code.includes("HKS") || code.includes("HKH");

const calculateFavoriteRunner = (races: RaceProgram[]): BettingInterest[][] => {
  const bettingInterests = getBettingInterests(races);
  try {
    if (bettingInterests.length) {
      return bettingInterests.map((bettingInterestsList) => {
        const useLowestOddFavorite =
          isFavoriteBIScratched(bettingInterestsList);
        const isHongKong = isHongKongRaces(get(races, "[0].id", ""));
        const processBIs = isHongKong
          ? BetSelectionUtils.filterHKCoupledEntries(bettingInterestsList)
          : bettingInterestsList;

        return processBIs.map((bettingInterest) => {
          if (get(bettingInterest, "runners.length")) {
            if (useLowestOddFavorite) {
              const lowestOdd = getLowestOdd(bettingInterestsList);

              return {
                ...bettingInterest,
                isFavorite: isFavorite(bettingInterest.currentOdds, lowestOdd)
              };
            }
            return {
              ...bettingInterest,
              isFavorite: bettingInterest.favorite
            };
          }
          return bettingInterest;
        });
      });
    }
  } catch (e) {
    console.error(e);
  }
  return [];
};

export default {
  skip: (props: RaceCardProps) =>
    props.isLoadingPP || !get(props.race, "track"),
  options: (props: RaceCardProps) => {
    const variables = {
      ...QUERY_VARIABLES,
      trackAbbr: get(props, "race.track.trackCode", "").toUpperCase(),
      wagerProfile: get(props, "wagerProfile", ""),
      raceNumber: +get(props, "race.raceNumber", ""),
      numOfRaces: +get(props, "wagerType.legCount", 1)
    };

    const poll = get(props, "hasWebSockets", false)
      ? {}
      : { pollInterval: 30000 };

    return {
      ...poll,
      fetchPolicy: "cache-first",
      ssr: false,
      variables
    };
  },
  props: (result: OperationComponent<Response>) => {
    return {
      bettingInterests: calculateFavoriteRunner(
        get(result, "data.track.races")
      ),
      isLoading: result.data.loading,
      subscribeToMore:
        get(result, "ownProps.hasWebSockets", false) && !result.data.loading
          ? result.data.subscribeToMore
          : null
    };
  }
};
